import { ChatCircleDotsIcon } from "@phosphor-icons/react/dist/csr/ChatCircleDots";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useContext } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import {
  ItemActionContext,
  STATEMENT_ITEM_ACTION_INTENT,
  STATEMENT_ITEM_MARK,
  referenceKey,
  statementRowKey,
} from "./statement-item-action";
import type { StatementItemReference } from "./statement-item-reference";

export const StatementItemActionButton = (
  reference: StatementItemReference,
) => {
  const action = useContext(ItemActionContext);
  if (!action) return null;
  if (!reference.line && !reference.heading) return null;

  const key = referenceKey(reference);
  const active = action.activeKey === key;
  const mark = action.marks?.[statementRowKey(reference)];
  if (action.marksOnly && !mark) return null;
  const name = reference.line || reference.heading || "this figure";
  const startsThread =
    action.intent === STATEMENT_ITEM_ACTION_INTENT.startThread;
  const label = mark
    ? `${mark === STATEMENT_ITEM_MARK.open ? "Open" : "Answered"} question about ${name}`
    : startsThread
      ? `Start a thread about ${name}`
      : `Question ${name}`;
  const hint = mark
    ? `${mark === STATEMENT_ITEM_MARK.open ? "Open question" : "Answered question"} about ${name}. Click to read it.`
    : active
      ? startsThread
        ? `This thread will be about ${name}.`
        : `This thread is about ${name}.`
      : startsThread
        ? `Start a thread about ${name}.`
        : `Ask about ${name}.`;

  return (
    <Tooltip>
      {/* The button itself is the trigger, not a wrapper: this mark sits inside
          the value's own <p>, and a wrapper div there is invalid nesting — React
          says so and the browser silently closes the paragraph early. The button
          is never disabled, which is the case a wrapper exists for. */}
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => action.onAction(reference)}
            aria-label={label}
            aria-pressed={"activeKey" in action ? active : undefined}
            className={cn("ml-1 align-middle text-subtle", {
              "bg-selected text-brand hover:bg-selected-hovered": active,
            })}
          />
        }
      >
        {/* A marked row says what state its conversation is in, in the same two
              marks PackChat uses everywhere else: an amber dot while it is open, a
              green check once it is answered. */}
        {mark === STATEMENT_ITEM_MARK.open ? (
          <span
            className="block size-[7px] rounded-full bg-accent-amber-bold"
            aria-hidden
          />
        ) : mark === STATEMENT_ITEM_MARK.closed ? (
          <CheckIcon className="size-3.5 text-success" aria-hidden />
        ) : (
          <ChatCircleDotsIcon className="size-4" aria-hidden />
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{hint}</TooltipContent>
    </Tooltip>
  );
};
